import { Driver } from "@lib/types/driver";
import { Checkbox } from "@mui/material";
import { ColumnDef } from "@tanstack/react-table";
import { ArrowDownUp, Eye, Pencil, X } from "lucide-react";
import i18n from "@lib/i18n";
import ActionButton from "@components/ActionButton";
import { cn } from "@lib/utils/tailwind";

export const columns = (
  setDriverToEdit: React.Dispatch<React.SetStateAction<Driver | undefined>>,
  setDialogOpen: React.Dispatch<React.SetStateAction<boolean>>,
  deleteDriver: (driver: Driver) => void,
  viewDriver: (driver: Driver) => void,
  setSyncDriverDialogOpen: (open: boolean) => void
): ColumnDef<Driver>[] => [
  {
    id: "select",
    header: ({ table }) => (
      <Checkbox
        checked={table.getIsAllRowsSelected()}
        indeterminate={table.getIsSomeRowsSelected()}
        onChange={() => table.toggleAllRowsSelected()}
        aria-label="Select all"
      />
    ),
    cell: ({ row }) => (
      <Checkbox
        checked={row.getIsSelected()}
        onChange={() => row.toggleSelected()}
        aria-label="Select row"
      />
    ),
    enableSorting: false,
    enableHiding: false,
    meta: { hideOnSmallScreens: true },
  },
  {
    accessorKey: "name",
    header: i18n.t("table.name"),
    cell: ({ row }) => (
      <span className="text-neutral-600 leading-none font-book text-base">
        {row.original.name}
      </span>
    ),
    enableSorting: true,
  },

  {
    accessorKey: "description",
    header: i18n.t("table.description"),
    cell: ({ row }) => (
      <span className="text-neutral-600 leading-none font-book text-base">
        {row.original.description}
      </span>
    ),
    enableSorting: true,
    meta: { hideOnSmallScreens: true },
  },
  {
    accessorKey: "code",
    header: i18n.t("table.code"),
    cell: ({ row }) => (
      <span className="text-neutral-600 leading-none font-book text-base">
        {row.original.code}
      </span>
    ),
    enableSorting: true,
    meta: { hideOnSmallScreens: true },
  },
  {
    accessorKey: "parameter_number",
    header: i18n.t("table.parameterNumber"),
    cell: ({ row }) => (
      <span className="text-neutral-600 leading-none font-book text-base">
        {row.original.parameter_number}
      </span>
    ),
    enableSorting: true,
  },
  {
    id: "actions",
    header: i18n.t("table.actions"),
    enableHiding: false,
    cell: ({ row }) => {
      return (
        <div className="flex w-full items-center justify-center gap-1">
          <ActionButton
            title={i18n.t("general.view")}
            onClick={() => viewDriver(row.original)}
          >
            <Eye size="20" />
          </ActionButton>

          {row.original.id ? (
            <ActionButton
              title={i18n.t("general.edit")}
              onClick={() => {
                setDriverToEdit(row.original);
                setDialogOpen(true);
              }}
              disabled={!row.original.id}
            >
              <Pencil size="20" />
            </ActionButton>
          ) : (
            <ActionButton
              title={i18n.t("general.assignSlotNumber")}
              onClick={() => {
                setDriverToEdit(row.original);
                setSyncDriverDialogOpen(true);
              }}
              disabled={!!row.original.id}
            >
              <ArrowDownUp size="20" />
            </ActionButton>
          )}

          <ActionButton
            title={i18n.t("general.delete")}
            onClick={() => {
              deleteDriver(row.original);
            }}
            className={cn(row.original.id ? "block" : "opacity-0")}
            disabled={!row.original.id}
          >
            <X size="20" color="red" />
          </ActionButton>
        </div>
      );
    },
  },
];
